import { create } from 'zustand';
import { GenerationStatus } from '../../types';

export interface ContentGenerationStatus {
  skuContentGenerationId: string;
  status: GenerationStatus;
}

export interface ContentGenerationStore {
  selectedItems: Set<string>;
  toggleItem: (retailerSku: string) => void;
  setSelectedItems: (items: Set<string>) => void;
  clearSelection: () => void;
  deselectAll: () => void;
  isGeneratingModalOpen: boolean;
  openGeneratingModal: () => void;
  closeGeneratingModal: () => void;
  watchedContentGenerationStatuses: ContentGenerationStatus[];
  setWatchedContentGenerationStatuses: (statuses: ContentGenerationStatus[]) => void;
  updateContentGenerationStatus: (skuContentGenerationId: string, status: GenerationStatus) => void;
}

/**
 * Store builder that creates isolated zustand store instances for product tables.
 * This allows multiple tables (e.g., modal vs main page) to have independent state.
 * Note: Currently not in use as we're using plain React state for the modal instead.
 */
export const createContentGenerationStore = () => {
  return create<ContentGenerationStore>((set, get) => ({
    selectedItems: new Set<string>(),

    toggleItem: (retailerSku) =>
      set((state) => {
        const newSelectedItems = new Set(state.selectedItems);

        if (newSelectedItems.has(retailerSku)) {
          newSelectedItems.delete(retailerSku);
        } else {
          newSelectedItems.add(retailerSku);
        }

        return { selectedItems: newSelectedItems };
      }),

    setSelectedItems: (items) => set({ selectedItems: items }),
    clearSelection: () => set({ selectedItems: new Set<string>() }),
    deselectAll: () => set({ selectedItems: new Set<string>() }),
    isGeneratingModalOpen: false,
    openGeneratingModal: () => set({ isGeneratingModalOpen: true }),
    closeGeneratingModal: () => set({ isGeneratingModalOpen: false, watchedContentGenerationStatuses: [] }),
    watchedContentGenerationStatuses: [],
    setWatchedContentGenerationStatuses: (statuses) => set({ watchedContentGenerationStatuses: statuses }),
    updateContentGenerationStatus: (skuContentGenerationId, status) =>
      set((state) => ({
        watchedContentGenerationStatuses: state.watchedContentGenerationStatuses.map((item) =>
          item.skuContentGenerationId === skuContentGenerationId
            ? { ...item, status }
            : item
        )
      }))
  }));
};

// Default global store instance for backward compatibility
export const useContentGenerationStore = createContentGenerationStore();
